import React, { useState, useEffect } from 'react';

const tierColors = {
  PLATINUM: { bg: '#f3e8ff', text: '#6b21a8', border: '#c084fc' },
  GOLD:     { bg: '#fef9c3', text: '#a16207', border: '#facc15' },
  SILVER:   { bg: '#f1f5f9', text: '#475569', border: '#94a3b8' },
  BRONZE:   { bg: '#fef3c7', text: '#92400e', border: '#f59e0b' },
  NEW:      { bg: '#eff6ff', text: '#1d4ed8', border: '#60a5fa' },
};
const priorityColors = { HIGH: '#dc2626', MEDIUM: '#ca8a04', LOW: '#16a34a' };

/**
 * Compact Guest Intelligence Card for embedding in check-in flows.
 * Props: guestId OR mobileNumber, chainId, propertyId, token, onViewFull (optional callback)
 */
function GuestIntelligenceCard({ guestId, mobileNumber, chainId, propertyId, token, onViewFull }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if ((!guestId && !mobileNumber) || !propertyId || !token) return;
    let cancelled = false;
    const hdrs = {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      'x-chain-id': chainId || '',
      'x-property-id': propertyId || '',
    };
    const load = async () => {
      setLoading(true); setError('');
      try {
        let resolvedGuestId = guestId;
        // If no guestId, lookup by mobile
        if (!resolvedGuestId && mobileNumber) {
          const lookupRes = await fetch(`/api/v1/ai/guest-recognition/lookup?mobile=${encodeURIComponent(mobileNumber)}`, { headers: hdrs });
          if (!lookupRes.ok) return;
          const lookupData = await lookupRes.json();
          if (!lookupData.found || !lookupData.guests?.length) return;
          resolvedGuestId = lookupData.guests[0].guest_id;
        }
        if (!resolvedGuestId) return;

        const res = await fetch(`/api/v1/ai/guest-recognition/profile/${resolvedGuestId}`, { headers: hdrs });
        if (!res.ok) throw new Error('Failed to load profile');
        const data = await res.json();
        if (!cancelled) setProfile(data.profile);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [guestId, mobileNumber, chainId, propertyId, token]);

  if (loading) return <div style={s.container}><div style={{ color: '#94a3b8', fontSize: 13 }}>Loading guest intelligence...</div></div>;
  if (error || !profile) return null; // Graceful — show nothing if no profile

  const tier = profile.loyalty_tier || 'NEW';
  const tc = tierColors[tier] || tierColors.NEW;
  const recommendations = (profile.ai_recommendations || []).slice(0, 3);

  return (
    <div style={s.container}>
      <div style={s.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#475569' }}>Guest Intelligence</span>
          <span style={{
            display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700,
            background: tc.bg, color: tc.text, border: `1px solid ${tc.border}`
          }}>{tier}</span>
          <span style={{ fontSize: 12, color: '#94a3b8' }}>{profile.total_stays || 0} stays</span>
        </div>
        {onViewFull && (
          <button onClick={onViewFull} style={s.linkBtn}>View Full Profile</button>
        )}
      </div>

      {profile.guest_profile_summary && (
        <div style={s.summary}>{profile.guest_profile_summary}</div>
      )}

      {recommendations.length > 0 && (
        <div style={s.recList}>
          {recommendations.map((rec, i) => (
            <div key={i} style={s.recItem}>
              <span style={{
                display: 'inline-block', width: 6, height: 6, borderRadius: 3, marginRight: 6, marginTop: 5,
                background: priorityColors[rec.priority] || '#94a3b8', flexShrink: 0
              }} />
              <span style={{ color: '#334155', fontSize: 13 }}>{rec.recommendation}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const s = {
  container: {
    background: '#faf5ff', border: '1px solid #e9d5ff', borderRadius: 8, padding: 12, marginTop: 12, marginBottom: 8
  },
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6
  },
  summary: {
    fontSize: 13, color: '#475569', lineHeight: 1.5, marginBottom: 8, fontStyle: 'italic'
  },
  recList: { display: 'flex', flexDirection: 'column', gap: 4 },
  recItem: { display: 'flex', alignItems: 'flex-start', fontSize: 13 },
  linkBtn: {
    background: 'none', border: 'none', color: '#7c3aed', cursor: 'pointer', fontSize: 12, fontWeight: 600, textDecoration: 'underline'
  }
};

export default GuestIntelligenceCard;
